/**
 * Query key factory with hybrid scoping
 * 
 * HYBRID SCOPING APPROACH:
 * - Club-scoped: Data specific to a location (bookings, bays, memberships)
 * - Tenant-scoped: Data shared across clubs (user profile, payment methods)
 * - Global: Auth session data (no scope needed)
 * 
 * SCOPING DECISION RULES:
 * 
 * Use clubId when:
 * ✅ Data is location-specific (bookings, bays, club-specific memberships)
 * ✅ Has clubId foreign key in database schema
 * ✅ User frequently switches between clubs
 * 
 * Use tenantHandle when:
 * ✅ Data is shared across all clubs (user profile, payment methods)
 * ✅ No clubId in database schema
 * ✅ Should persist when user switches clubs
 * 
 * Use neither when:
 * ✅ Global auth/session data
 * ✅ No tenant or club association
 * 
 * CACHE INVALIDATION:
 * - Club change → Invalidate club-scoped queries only
 * - Tenant change → Nuclear option, clear everything
 */
export const queryKeys = {
  // ============================================================================
  // GLOBAL SCOPE (no tenant/club - auth/session data)
  // ============================================================================
  auth: {
    currentUser: () => ['auth', 'currentUser'] as const,
    session: () => ['auth', 'session'] as const,
  },

  // ============================================================================
  // TENANT-SCOPED (data shared across all clubs under a tenant)
  // ============================================================================
  tenant: {
    // User profile (same across all clubs)
    user: (tenantHandle: string, userId: number) => 
      ['tenantHandle', tenantHandle, 'user', userId] as const,
    
    // User details (for account setup)
    userDetail: (tenantHandle: string, userId: string | number) => 
      ['tenantHandle', tenantHandle, 'user', 'detail', String(userId)] as const,
    
    // Payment methods (shared across clubs)
    paymentMethods: (tenantHandle: string, userId: number) => 
      ['tenantHandle', tenantHandle, 'paymentMethods', userId] as const,
    
    // Single payment method (shared across clubs)
    paymentMethod: (tenantHandle: string, userId: number, paymentMethodId: number) => 
      ['tenantHandle', tenantHandle, 'paymentMethod', userId, paymentMethodId] as const,
    
    // User's clubs within tenant
    userClubs: (tenantHandle: string, userId: number) => 
      ['tenantHandle', tenantHandle, 'userClubs', userId] as const,

    // Account credit balance (per user, shared across clubs — credits D5)
    credit: (tenantHandle: string, userId: number) =>
      ['tenantHandle', tenantHandle, 'credit', userId] as const,
  },

  // ============================================================================
  // CLUB-SCOPED (data specific to a location)
  // ============================================================================
  
  // Club domain (location data)
  // Note: No `all()` key - use urlChangeInvalidator plugin for club switching
  // Individual queries are club-scoped with ['clubId', clubId, 'club', ...]
  club: {
    detail: (clubId: number) => ['clubId', clubId, 'club', 'detail'] as const,
    status: (clubId: number) => ['clubId', clubId, 'club', 'status'] as const,
    paymentSettings: (clubId: number) => ['clubId', clubId, 'club', 'paymentSettings'] as const,
  },

  // Bookings (bay bookings are location-specific)
  bookings: {
    all: (clubId: number) => ['clubId', clubId, 'bookings'] as const,
    list: (clubId: number, userId?: number) => 
      userId 
        ? ['clubId', clubId, 'bookings', 'user', userId] as const
        : ['clubId', clubId, 'bookings', 'list'] as const,
    // Booking detail is NOT club-scoped because booking IDs are globally unique.
    // Using a club-independent key means the same booking is always cached under
    // a single entry regardless of how the page is reached (post-creation flow vs
    // direct confirmation URL). All mutations that affect a booking must explicitly
    // invalidate this key in addition to the club-scoped list keys.
    detail: (bookingId: string | number) =>
      ['bookings', 'detail', String(bookingId)] as const,
    // One more block on offer for a booking in progress (Simulator Companion slice 9).
    // Under the detail key, so invalidating the booking refreshes its offer too.
    extensionOffer: (bookingId: string | number) =>
      ['bookings', 'detail', String(bookingId), 'extension-offer'] as const,
    // Booking validation (checks subscription limits, availability, etc.)
    // `promoCode` is part of the key, and has to be: one entry cannot serve both
    // the discounted answer and the list-price one. Same rule the subscription
    // quote key carries, for the same reason.
    validation: (clubId: number, params: { bayId: number; startTime: string; endTime: string; userId?: number; guestCount?: number; promoCode?: string }) =>
      ['clubId', clubId, 'bookings', 'validation', params] as const,
    // Guest (unauthenticated) price quote. A DIFFERENT key from `validation` on
    // purpose — different endpoint, different payload, and no identity in it, so a
    // guest signing in mid-flow moves to a fresh entry rather than reading a cached
    // anonymous price.
    guestQuote: (clubId: number, params: { bayId: number; startTime: string; endTime: string; guestCount?: number; promoCode?: string }) =>
      ['clubId', clubId, 'bookings', 'guestQuote', params] as const,
    availability: (clubId: number, date?: string) => 
      date
        ? ['clubId', clubId, 'bookings', 'availability', date] as const
        : ['clubId', clubId, 'bookings', 'availability'] as const,
  },

  // Bays (physical bays at specific location)
  bays: {
    all: (clubId: number) => ['clubId', clubId, 'bays'] as const,
    list: (clubId: number) => ['clubId', clubId, 'bays', 'list'] as const,
    detail: (clubId: number, bayId: number) => 
      ['clubId', clubId, 'bays', 'detail', bayId] as const,
    // Availability must include all query parameters to prevent cache collisions
    // Different bayId/date/time combinations must have unique cache entries
    availability: (clubId: number, bayId: number, date: string, startTime?: string, endTime?: string) => 
      ['clubId', clubId, 'bays', 'availability', bayId, date, startTime, endTime] as const,
    // Bay availability prefix for invalidation (all times on a specific bay/date)
    // Use this when you need to invalidate ALL availability queries for a bay on a given date,
    // regardless of startTime/endTime. This prevents key mismatch issues where:
    // - Query key: ['clubId', 1, 'bays', 'availability', 2, '2025-12-16', '10:00', '11:00']
    // - Invalidation key: ['clubId', 1, 'bays', 'availability', 2, '2025-12-16', undefined, undefined]
    // Would NOT match because undefined !== '10:00'
    availabilityForDate: (clubId: number, bayId: number, date: string) =>
      ['clubId', clubId, 'bays', 'availability', bayId, date] as const,
    // Available bays with duration-based checking (replaces deprecated availableBaysKeys)
    available: (clubId: number, params: {
      startTime?: string;
      endTime?: string;
      userId?: number;
      bayIds?: number[];
      collectAllRestrictions?: boolean;
      slotIntervalMinutes?: number;
      /** In the key so the demo grid never cross-serves cached standard availability. */
      type?: 'demo';
    }) =>
      ['clubId', clubId, 'bays', 'available', params] as const,
    // Club-wide availability (multi-bay query)
    // userId included because availability results differ based on subscription status
    // `type` in the key so the demo grid never cross-serves cached standard availability.
    clubAvailability: (clubId: number, date: string, bayId?: number, durations?: string, userId?: number, slotInterval?: number, type?: 'demo') =>
      ['clubId', clubId, 'bays', 'clubAvailability', date, bayId, durations, userId, slotInterval, type] as const,
  },

  // Subscriptions (club-specific) - hybrid queries for memberships OR passes
  subscriptions: {
    // Get active subscription (membership OR pass) for a user
    active: (clubId: number, userId: number) => 
      ['clubId', clubId, 'subscriptions', 'active', 'user', userId] as const,
    // All subscriptions at club level (for invalidating available subscriptions)
    all: (clubId: number) => 
      ['clubId', clubId, 'subscriptions'] as const,
    // Available subscriptions for purchase (memberships + passes offered at this club)
    available: (clubId: number) => 
      ['clubId', clubId, 'subscriptions', 'available'] as const,
    // Server-authored purchase quote (D12).
    //
    // `asMember` IS part of the key, and this reverses the rule that stood
    // here before. The old comment said identity must stay out because "the
    // price is identity-independent" — true while the only route was
    // tenant-only, and false now that `/subscriptions/quote/me` evaluates a
    // promo code against the caller: a code capped per customer applies for
    // one member and not another, so the guest answer and the member answer
    // are different server answers and must not share an entry.
    //
    // The worry the old rule was protecting against inverts, too. It feared
    // that keying on identity would let a mid-stream sign-in miss the cache;
    // now that miss is exactly right — the guest funnel authenticates between
    // the Review and FinishAccount steps, and the refetch it triggers is what
    // shows the buyer a cap they had not been told about.
    //
    // A boolean rather than the user id: the id would fragment the cache per
    // member for no gain (a browser session is one member) and would put an
    // identifier in a devtools-visible key.
    //
    // `promoCode` MUST be part of the key when present: after an auto-drop the
    // list-price quote and the discounted quote are different server answers —
    // one cache entry serving both would show the dropped discount (or hide a
    // live one).
    quote: (
      clubId: number,
      params: {
        subscriptionType: string;
        subscriptionId: number;
        promoCode?: string;
        quantity?: number;
        asMember?: boolean;
      },
    ) => ['clubId', clubId, 'subscriptions', 'quote', params] as const,
    /** Gift eligibility for one committed address — a server answer, so it lives here. */
    giftEligibility: (clubId: number, email: string) =>
      ['clubId', clubId, 'subscriptions', 'gift-eligibility', email] as const,
  },

  // Memberships (club-specific) - membership-only queries
  memberships: {
    all: (clubId: number) => ['clubId', clubId, 'memberships'] as const,
    list: (clubId: number) => ['clubId', clubId, 'memberships', 'list'] as const,
    // Keyed by VIEWER as well as id. `/memberships/details/:id` answers
    // differently per caller once a plan carries a new-member anchor, so a
    // guest's 404 must never be served to a signed-in member. The viewer
    // segment makes that structural: the key changes when the session does,
    // and the refetch follows for free. See `usePlanDetails`.
    // `viewerId` is whatever the auth layer holds (it types the id as
    // `string | number`) — an opaque cache discriminator, never arithmetic.
    detail: (clubId: number, membershipId: string, viewerId: string | number | null) =>
      ['clubId', clubId, 'memberships', 'detail', membershipId, viewerId] as const,
  },

  // Passes (club-specific)
  passes: {
    all: (clubId: number) => ['clubId', clubId, 'passes'] as const,
    list: (clubId: number, userId: number) => 
      ['clubId', clubId, 'passes', 'user', userId] as const,
    // Keyed by VIEWER as well as id — see `memberships.detail` above for why.
    detail: (clubId: number, passId: string, viewerId: string | number | null) =>
      ['clubId', clubId, 'passes', 'detail', passId, viewerId] as const,
    // User's purchased pass details (for confirmation page)
    userDetail: (clubId: number, userId: number, userPassId: number) =>
      ['clubId', clubId, 'passes', 'user', userId, 'detail', userPassId] as const,
  },

  // Lessons (club-specific). Later slices append here, not a second group.
  lessons: {
    all: (clubId: number) => ['clubId', clubId, 'lessons'] as const,
    // The catalogue a guest gets and the one a member gets are different
    // server answers: a package hidden from new members is still listed for
    // someone who joined before it was hidden. So the two must not share an
    // entry, or whichever was fetched first is shown to both. A boolean, not
    // the user id, for the reason given on `subscriptions.quote` above.
    available: (clubId: number, params: { asMember: boolean }) =>
      ['clubId', clubId, 'lessons', 'available', params] as const,
    /** The packs this member holds at `clubId` — GET /lessons/user/:userId */
    user: (clubId: number, userId: number) =>
      ['clubId', clubId, 'lessons', 'user', userId] as const,
    /** Instructors bookable for a DIRECT lesson — GET /lessons/club/:clubId/instructors */
    instructors: (clubId: number) => ['clubId', clubId, 'lessons', 'instructors'] as const,
    /** One instructor's grid for one day — GET /lessons/club/:clubId/availability */
    availability: (
      clubId: number,
      params: { instructorId: number; date: string; durations: readonly number[]; slotInterval: number },
    ) => ['clubId', clubId, 'lessons', 'availability', params] as const,
    /** What one slot would cost this member — POST /lesson-bookings/quote */
    bookingQuote: (
      clubId: number,
      params: { instructorId: number; bayId: number; startTime: string; endTime: string },
    ) => ['clubId', clubId, 'lessons', 'bookingQuote', params] as const,
  },

  // Invoices
  invoices: {
    all: (clubId: number) => ['clubId', clubId, 'invoices'] as const,
    list: (clubId: number, userId: number) =>
      ['clubId', clubId, 'invoices', 'user', userId] as const,
    // Invoice detail is NOT club-scoped because invoice IDs are globally unique.
    // Same rationale as bookings.detail: a single cache entry regardless of whether
    // clubId is available (post-purchase flow vs. direct confirmation URL visit).
    detail: (invoiceId: string | number) =>
      ['invoices', 'detail', invoiceId] as const,
  },

  // Announcements (club-specific)
  // @planned - No hooks using these keys yet. Reserved for future announcement features.
  announcements: {
    all: (clubId: number) => ['clubId', clubId, 'announcements'] as const,
    list: (clubId: number) => ['clubId', clubId, 'announcements', 'list'] as const,
  },

  // Waivers (club-specific) — the documents members sign before booking
  waiver: {
    all: (clubId: number) => ['clubId', clubId, 'waiver'] as const,
    active: (clubId: number) => ['clubId', clubId, 'waiver', 'active'] as const,
    /** This member's outstanding waivers — drives the entry gate. */
    standing: (clubId: number) => ['clubId', clubId, 'waiver', 'standing'] as const,
  },

  // ============================================================================
  // UNLOCK LINK (no scope — this page has no session, tenant or club at mount)
  // ============================================================================
  // Scoped by a FINGERPRINT of the door token, never the token itself. Nothing persists
  // the query cache today, so a raw token in the key is harmless right now — and becomes a
  // durable credential leak the day anyone adds `persistQueryClient`, because keys are what
  // a persister writes to localStorage. A fingerprint costs nothing and cannot become that.
  unlockLink: {
    exchange: (tokenFingerprint: string) => ['unlockLink', tokenFingerprint] as const,
  },

  // ============================================================================
  // DISABLED STATE KEYS (when queries are not enabled)
  // ============================================================================
  // Standardized disabled keys to prevent cache collisions
  // Format: ['disabled', domain, ...specificity]
  disabled: {
    credit: () => ['disabled', 'credit'] as const,
    club: () => ['disabled', 'club'] as const,
    clubPaymentSettings: () => ['disabled', 'club', 'paymentSettings'] as const,
    clubStatus: () => ['disabled', 'club', 'status'] as const,
    bays: () => ['disabled', 'bays'] as const,
    bayAvailability: () => ['disabled', 'bays', 'availability'] as const,
    bayAvailable: () => ['disabled', 'bays', 'available'] as const,
    clubAvailability: () => ['disabled', 'bays', 'clubAvailability'] as const,
    bookings: () => ['disabled', 'bookings'] as const,
    extensionOffer: () => ['disabled', 'bookings', 'extension-offer'] as const,
    unlockLink: () => ['disabled', 'unlockLink'] as const,
    bookingValidation: () => ['disabled', 'bookings', 'validation'] as const,
    bookingQuote: () => ['disabled', 'bookings', 'guestQuote'] as const,
    subscriptionQuote: () => ['disabled', 'subscriptions', 'quote'] as const,
    giftEligibility: () => ['disabled', 'subscriptions', 'gift-eligibility'] as const,
    invoices: () => ['disabled', 'invoices'] as const,
    subscriptions: () => ['disabled', 'subscriptions'] as const,
    subscriptionsAvailable: () => ['disabled', 'subscriptions', 'available'] as const,
    passes: () => ['disabled', 'passes'] as const,
    lessons: () => ['disabled', 'lessons'] as const,
    memberships: () => ['disabled', 'memberships'] as const,
    waiver: () => ['disabled', 'waiver'] as const,
    user: () => ['disabled', 'user'] as const,
    paymentMethods: () => ['disabled', 'paymentMethods'] as const,
    paymentMethod: () => ['disabled', 'paymentMethod'] as const,
  },
};
